import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogContentText, DialogTitle, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import KeyIcon from '@mui/icons-material/Key';
import BuyAccessCodeButton from '../../../components/BuyAccessCodeButton.js';
import CreditConnectionPurchase from '../../../components/CreditConnectionPurchase.js';
import { authedFetch } from '../../../utils/backendFetch.js';
import { creditWalletOperationCompleted } from '../../../creditWallet/WalletBalanceContext.js';
import { PHASE45_COMMS_ENABLED, PHASE45_COPY } from '../../../constants/phase45Comms.js';
import {
  CREDIT_CONNECTION_PRICE_CREDITS,
  cancelTherapistAccessCode,
  checkTierCapacity,
  deriveCurrentTier,
  listTherapistAccessCodes,
} from '../../../utils/creditWalletRpc.js';

// Management surface for MindVault client access codes (pricing/access model
// Phase 3): list the therapist's codes with billing status, buy a new one for
// a client email, cancel an existing one. Backed by the RAG backend
// /stripe/access-codes endpoints; a code connects only after the client also
// consents in the MindVault app.

const STATUS_CHIP = {
  active:   { label: 'Active',            color: '#DCFCE7', text: '#15803D' },
  pending:  { label: 'Awaiting payment',  color: '#FEF9C3', text: '#A16207' },
  grace:    { label: 'Payment failed — update card', color: '#FFEDD5', text: '#C2410C' },
  past_due: { label: 'Payment past due',  color: '#FFEDD5', text: '#C2410C' },
  canceled: { label: 'Canceled',          color: '#F3F4F6', text: '#6B7280' },
};

// Phase 5 credit-connections list: labels for the display_status values the
// authority (list_therapist_access_codes) derives. Liveness comes ONLY from
// that RPC — never re-derived here. Credit grace is a short-wallet renewal
// state, so its label is neutral (no card-dunning vocabulary).
const DISPLAY_STATUS_CHIP = {
  active:         { label: 'Active', color: '#DCFCE7', text: '#15803D' },
  grace:          { label: 'In grace — renewal pending', color: '#FFEDD5', text: '#C2410C' },
  over_cap_grace: { label: 'Over capacity — in grace', color: '#FFEDD5', text: '#C2410C' },
  pending:        { label: 'Pending', color: '#FEF9C3', text: '#A16207' },
  canceled:       { label: 'Canceled', color: '#F3F4F6', text: '#6B7280' },
  inactive:       { label: 'Inactive', color: '#F3F4F6', text: '#6B7280' },
};

const FUNDING_CHIP = {
  credits: { label: 'Credits',       color: '#E6F7F6', text: '#0F766E' },
  stripe:  { label: 'Plan billing',  color: '#EFF6FF', text: '#1D4ED8' },
};

// Whether a live code actually connects the client (backend `connection`
// field). A code can bill while never connecting — surface that instead of
// letting it hide behind a green "Active" chip.
const CONNECTION_CHIP = {
  booking:     { label: 'Connected (booking)', color: '#DCFCE7', text: '#15803D' },
  access_code: { label: 'Connected',           color: '#DCFCE7', text: '#15803D' },
  subscription: { label: 'Connected (plan)',   color: '#DCFCE7', text: '#15803D' },
  no_account:  { label: 'Awaiting MindVault sign-up', color: '#FEF9C3', text: '#A16207' },
  no_consent:  { label: 'Awaiting client consent',    color: '#FEF9C3', text: '#A16207' },
};

// cents + ISO currency -> "HK$120.00" style label
const formatMoney = (cents, currency) => {
  if (cents == null) return null;
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: (currency || 'usd').toUpperCase(),
    }).format(cents / 100);
  } catch {
    return `${(cents / 100).toFixed(2)} ${(currency || '').toUpperCase()}`;
  }
};

const AccessCodesSection = ({ userDetails }) => {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [buyEmail, setBuyEmail] = useState('');
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const [summary, setSummary] = useState(null);
  // Phase 5 credit surfaces (flag-gated): the credit-add form, the
  // credit-connections list, and the capacity pre-check for the form.
  const [creditEmail, setCreditEmail] = useState('');
  const [creditCodes, setCreditCodes] = useState([]);
  const [creditLoading, setCreditLoading] = useState(false);
  const [creditError, setCreditError] = useState(null);
  const [creditCancelTarget, setCreditCancelTarget] = useState(null);
  const [creditCancelling, setCreditCancelling] = useState(false);
  const [capacityInfo, setCapacityInfo] = useState(null);

  // Billing summary is nice-to-have context — failures stay silent so they
  // never block the code list itself.
  const fetchSummary = useCallback(async () => {
    if (!userDetails?.id) return;
    // Phase 5 §9: new fiat sales are retired at the flip and the backend
    // funding slice 410s create-checkout. This billing-summary fetch feeds
    // ONLY the legacy fiat purchase/billing panel (both hidden post-flip), so
    // skip the call while PHASE45_COMMS_ENABLED is on — it would otherwise hit
    // a retired endpoint. Existing fiat connections stay listed/cancellable
    // through fetchCodes + the legacy table below (legacy cancel stays live).
    if (PHASE45_COMMS_ENABLED) return;
    try {
      const response = await authedFetch(
        `${process.env.REACT_APP_STRIPE_CONNECT_URL}/stripe/access-codes/billing-summary`
      );
      if (!response.ok) throw new Error('billing summary unavailable');
      setSummary(await response.json());
    } catch (err) {
      console.warn('Access-code billing summary unavailable:', err.message);
      setSummary(null);
    }
  }, [userDetails?.id]);

  const fetchCodes = useCallback(async () => {
    if (!userDetails?.id) return;
    setLoading(true);
    setError(null);
    try {
      const response = await authedFetch(
        `${process.env.REACT_APP_STRIPE_CONNECT_URL}/stripe/access-codes?therapist_user_id=${userDetails.id}`
      );
      const data = await response.json();
      if (!response.ok) throw new Error(data?.error || 'Failed to load access codes');
      setCodes(data.access_codes || []);
    } catch (err) {
      console.error('Error loading access codes:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [userDetails?.id]);

  // Phase 5 (flag-gated): credit-connections list via the authority RPC.
  // Never runs while the flag is off, so no RPC fires from this surface
  // before the flip.
  const fetchCreditCodes = useCallback(async () => {
    if (!userDetails?.id) return;
    setCreditLoading(true);
    setCreditError(null);
    try {
      const { data, error: rpcError } = await listTherapistAccessCodes();
      if (rpcError) throw new Error(rpcError.message || 'Failed to load credit connections');
      setCreditCodes(data || []);
    } catch (err) {
      console.error('Error loading credit connections:', err);
      setCreditError(err.message);
    } finally {
      setCreditLoading(false);
    }
  }, [userDetails?.id]);

  // Capacity pre-check for the credit-add form: derive the current tier
  // (the same derivation the RPC uses) and show the denial arithmetic when
  // the plan is full. The purchase RPC re-checks and remains authoritative.
  const refreshCapacity = useCallback(async () => {
    if (!userDetails?.id) return;
    const tier = await deriveCurrentTier(userDetails.id);
    if (!tier) return;
    const { data, error: rpcError } = await checkTierCapacity(tier);
    if (!rpcError) setCapacityInfo(data || null);
  }, [userDetails?.id]);

  useEffect(() => {
    fetchCodes();
    fetchSummary();
    if (PHASE45_COMMS_ENABLED) {
      fetchCreditCodes();
      refreshCapacity();
    }
  }, [fetchCodes, fetchSummary, fetchCreditCodes, refreshCapacity]);

  const handleCancelConfirm = async () => {
    if (!cancelTarget) return;
    setCancelling(true);
    try {
      const response = await authedFetch(
        `${process.env.REACT_APP_STRIPE_CONNECT_URL}/stripe/access-codes/${cancelTarget.id}/cancel`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ therapist_user_id: userDetails.id }),
        }
      );
      const data = await response.json();
      if (!response.ok) throw new Error(data?.error || 'Failed to cancel access code');
      setCancelTarget(null);
      await fetchCodes();
      fetchSummary();
    } catch (err) {
      console.error('Error cancelling access code:', err);
      setError(err.message);
      setCancelTarget(null);
    } finally {
      setCancelling(false);
    }
  };

  // Phase 5 (flag-gated): cancel a CREDIT connection through the RPC. Fiat
  // rows never reach this handler — their Cancel is disabled in the list.
  const handleCreditCancelConfirm = async () => {
    if (!creditCancelTarget) return;
    setCreditCancelling(true);
    try {
      const { error: rpcError } = await cancelTherapistAccessCode(creditCancelTarget.id);
      if (rpcError) throw new Error(rpcError.message || 'Failed to cancel credit connection');
      setCreditCancelTarget(null);
      await fetchCreditCodes();
      refreshCapacity();
      // A cancel frees a capacity slot and can change wallet-derived state;
      // fire the shared refresh event so the balance chip re-fetches without a
      // page reload (same convention as BuyCreditsDialog / CreditConnectionPurchase).
      creditWalletOperationCompleted();
    } catch (err) {
      console.error('Error cancelling credit connection:', err);
      setCreditError(err.message);
      setCreditCancelTarget(null);
    } finally {
      setCreditCancelling(false);
    }
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
        <KeyIcon sx={{ color: '#29C0BB' }} /> Client Connections
      </Typography>
      <Typography variant="body2" sx={{ color: '#6B7280', mb: 3, maxWidth: 640 }}>
        {/* TIME-BOMBED COPY: the legacy sentence "gives you unrestricted Scribe
            use for that client" becomes FALSE when Phase 4/5 flips (sessions
            then draw from the credit wallet). The replacement is a DRAFT
            (product/boss will revise after experience), gated by
            PHASE45_COMMS_ENABLED — while the flag is off this renders the
            legacy paragraph byte-identically. Do not enable the flag without
            the Phase 4/5 entry gates (see src/constants/phase45Comms.js). */}
        {PHASE45_COMMS_ENABLED
          ? PHASE45_COPY.connectionsParagraph.flipped
          : PHASE45_COPY.connectionsParagraph.legacy}
      </Typography>

      {/* Legacy fiat "buy a new access code" purchase — Phase 5 §9 retires new
          fiat sales at the flip. Gated OFF by PHASE45_COMMS_ENABLED: post-flip
          the backend funding slice already 410s create-checkout, so this
          NEW-purchase UI must not render (do not rely on hiding the button
          alone — the backend is the real gate). Existing fiat connections
          stay listed and cancellable in the access-codes table below, whose
          legacy cancel path remains enabled. */}
      {!PHASE45_COMMS_ENABLED && (
        <Paper variant="outlined" sx={{ p: 2.5, mb: 3, borderRadius: '12px' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
            Buy a new access code
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-start' }}>
            <TextField
              size="small"
              type="email"
              label="Client email"
              placeholder="client@example.com"
              value={buyEmail}
              onChange={(e) => setBuyEmail(e.target.value)}
              sx={{ minWidth: 280 }}
              helperText="Use the email the client registered (or will register) with MindVault"
            />
            <BuyAccessCodeButton
              therapistUserId={userDetails?.id}
              clientEmail={buyEmail.trim()}
              onPurchased={() => { setBuyEmail(''); fetchCodes(); fetchSummary(); }}
            />
          </Box>
        </Paper>
      )}

      {/* Phase 5 credit-purchase path — gated by PHASE45_COMMS_ENABLED; renders
          nothing while the flag is off. purchase_connection_with_credits runs
          through the frozen RPC with a client-side operation id, behind a
          confirm-before-debit dialog (see CreditConnectionPurchase.js); the
          capacity line below shows the
          denial arithmetic from check_tier_capacity when the plan is full. */}
      {PHASE45_COMMS_ENABLED && (
        <Paper variant="outlined" sx={{ p: 2.5, mb: 3, borderRadius: '12px' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
            Add client by email
          </Typography>
          <Typography variant="body2" sx={{ color: '#6B7280', mb: 1.5, maxWidth: 640 }}>
            Connects the client for {CREDIT_CONNECTION_PRICE_CREDITS} credits/month,
            drawn from your credit wallet and charged immediately. The connection is
            active from payment; the client must still consent to sharing in their
            MindVault app.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-start' }}>
            <TextField
              size="small"
              type="email"
              label="Client email"
              placeholder="client@example.com"
              value={creditEmail}
              onChange={(e) => setCreditEmail(e.target.value)}
              sx={{ minWidth: 280 }}
              helperText="Use the email the client registered (or will register) with MindVault"
            />
            <CreditConnectionPurchase
              clientEmail={creditEmail.trim()}
              userId={userDetails?.id}
              onPurchased={() => {
                setCreditEmail('');
                fetchCreditCodes();
                refreshCapacity();
                fetchCodes();
                // Refresh the wallet balance chip without a page reload. The
                // purchase debits credits; CreditConnectionPurchase already
                // fires this on success, and we re-assert it here so the
                // access-code surface's refresh is self-contained (the event is
                // idempotent — the context only accepts a newer wallet row).
                creditWalletOperationCompleted();
              }}
            />
          </Box>
          {capacityInfo && (
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                mt: 1.5,
                color: capacityInfo.allowed ? '#6B7280' : '#d32f2f',
              }}
            >
              {capacityInfo.allowed
                ? (capacityInfo.capacity == null
                    ? `${capacityInfo.active_credit_connections} credit-paid connection${capacityInfo.active_credit_connections === 1 ? '' : 's'} in use · limited only by your wallet balance`
                    : `${capacityInfo.active_credit_connections} of ${capacityInfo.capacity} credit-paid connection slots in use`)
                : capacityInfo.arithmetic}
            </Typography>
          )}
        </Paper>
      )}

      {/* Phase 5 credit-connections list — gated by PHASE45_COMMS_ENABLED; renders
          nothing while the flag is off. Rows come from list_therapist_access_codes
          (fiat AND credit — the RPC is the single liveness authority). Credit rows
          cancel through cancel_therapist_access_code; fiat rows show a disabled
          state instead of an error, because that RPC refuses fiat rows (55000). */}
      {PHASE45_COMMS_ENABLED && (
        <Paper variant="outlined" sx={{ p: 2.5, mb: 3, borderRadius: '12px' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
            Credit connections
          </Typography>
          {creditError && (
            <Typography variant="body2" sx={{ color: '#d32f2f', mb: 1.5 }}>{creditError}</Typography>
          )}
          {creditLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress sx={{ color: '#29C0BB' }} />
            </Box>
          ) : creditCodes.length === 0 ? (
            <Typography variant="body2" sx={{ color: '#9CA3AF', textAlign: 'center', p: 2 }}>
              No credit connections yet.
            </Typography>
          ) : (
            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: '12px' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Client</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Funding</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Renews</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Created</TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {creditCodes.map((code) => {
                    const statusChip = DISPLAY_STATUS_CHIP[code.display_status] || {
                      label: code.display_status || code.status || '—',
                      color: '#F3F4F6',
                      text: '#6B7280',
                    };
                    const fundingChip = FUNDING_CHIP[code.funding] || {
                      label: code.funding || '—',
                      color: '#F3F4F6',
                      text: '#6B7280',
                    };
                    const isCredit = code.funding === 'credits';
                    const cancelable = isCredit && code.display_status !== 'canceled';
                    return (
                      <TableRow key={code.id}>
                        <TableCell>{code.client_email || '—'}</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={statusChip.label}
                            sx={{ backgroundColor: statusChip.color, color: statusChip.text, fontWeight: 600 }}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={fundingChip.label}
                            sx={{ backgroundColor: fundingChip.color, color: fundingChip.text, fontWeight: 600 }}
                          />
                        </TableCell>
                        <TableCell>
                          {code.current_period_end
                            ? new Date(code.current_period_end).toLocaleDateString()
                            : '—'}
                        </TableCell>
                        <TableCell>
                          {code.created_at ? new Date(code.created_at).toLocaleDateString() : '—'}
                        </TableCell>
                        <TableCell align="right">
                          {isCredit ? (
                            <Button
                              size="small"
                              variant="outlined"
                              color="error"
                              sx={{ textTransform: 'none', borderRadius: '8px' }}
                              disabled={!cancelable}
                              onClick={() => setCreditCancelTarget(code)}
                            >
                              Cancel
                            </Button>
                          ) : (
                            <Tooltip title="This connection is billed through your plan; it cannot be canceled here.">
                              <span>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="error"
                                  disabled
                                  sx={{ textTransform: 'none', borderRadius: '8px' }}
                                >
                                  Cancel
                                </Button>
                              </span>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      )}

      {summary && (summary.next_invoice || Object.keys(summary.counts || {}).length > 0) && (
        <Paper variant="outlined" sx={{ p: 2.5, mb: 3, borderRadius: '12px' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
            Billing summary
          </Typography>
          <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            <Box>
              <Typography variant="caption" sx={{ color: '#6B7280' }}>Codes billing</Typography>
              <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                {['active', 'grace', 'pending'].map((s) => {
                  const n = summary.counts?.[s];
                  if (!n) return null;
                  const chip = STATUS_CHIP[s];
                  return (
                    <Chip
                      key={s}
                      size="small"
                      label={`${n} ${chip.label.toLowerCase()}`}
                      sx={{ backgroundColor: chip.color, color: chip.text, fontWeight: 600 }}
                    />
                  );
                })}
              </Box>
            </Box>
            <Box>
              <Typography variant="caption" sx={{ color: '#6B7280' }}>Next invoice</Typography>
              <Typography variant="body1" sx={{ fontWeight: 700, mt: 0.5 }}>
                {summary.next_invoice
                  ? formatMoney(summary.next_invoice.amount_due_cents, summary.next_invoice.currency)
                  : '—'}
              </Typography>
              {/* A total that isn't quantity x price needs explaining: a code
                  added mid-cycle bills a catch-up for the rest of the current
                  period on top of next month's charge. */}
              {!!summary.next_invoice?.proration_cents && (
                <Typography variant="caption" sx={{ color: '#6B7280', display: 'block' }}>
                  {formatMoney(summary.next_invoice.recurring_cents, summary.next_invoice.currency)}
                  {' monthly '}
                  {summary.next_invoice.proration_cents < 0 ? '−' : '+'}
                  {' '}
                  {formatMoney(
                    Math.abs(summary.next_invoice.proration_cents),
                    summary.next_invoice.currency
                  )}
                  {' proration'}
                </Typography>
              )}
            </Box>
            {summary.next_invoice?.period_end && (
              <Box>
                <Typography variant="caption" sx={{ color: '#6B7280' }}>Billing period ends</Typography>
                <Typography variant="body1" sx={{ fontWeight: 700, mt: 0.5 }}>
                  {new Date(summary.next_invoice.period_end).toLocaleDateString()}
                </Typography>
              </Box>
            )}
          </Box>
          <Typography variant="caption" sx={{ color: '#9CA3AF', display: 'block', mt: 1.5 }}>
            The estimate includes proration for codes added or cancelled mid-cycle.
          </Typography>
        </Paper>
      )}

      {error && (
        <Typography variant="body2" sx={{ color: '#d32f2f', mb: 2 }}>{error}</Typography>
      )}

      {/* Legacy fiat access-code list — hidden entirely once PHASE45_COMMS_ENABLED
          is on. Credit connections above are the only funding path post-flip; there
          is no real fiat data to wind down (fiat was only added during development). */}
      {!PHASE45_COMMS_ENABLED && (loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress sx={{ color: '#29C0BB' }} />
        </Box>
      ) : (PHASE45_COMMS_ENABLED
        ? codes.filter((code) => code.funding !== 'credits')
        : codes
      ).length === 0 ? (
        <Typography variant="body2" sx={{ color: '#9CA3AF', textAlign: 'center', p: 3 }}>
          No access codes yet.
        </Typography>
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: '12px' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Client</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Connection</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Renews</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Created</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {(PHASE45_COMMS_ENABLED
                ? codes.filter((code) => code.funding !== 'credits')
                : codes
              ).map((code) => {
                const chip = STATUS_CHIP[code.status] || STATUS_CHIP.canceled;
                const live = code.status !== 'canceled';
                const connChip = live ? CONNECTION_CHIP[code.connection] : null;
                return (
                  <TableRow key={code.id}>
                    <TableCell>{code.client_email || '—'}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={chip.label}
                        sx={{ backgroundColor: chip.color, color: chip.text, fontWeight: 600 }}
                      />
                    </TableCell>
                    <TableCell>
                      {connChip ? (
                        <Chip
                          size="small"
                          label={connChip.label}
                          sx={{ backgroundColor: connChip.color, color: connChip.text, fontWeight: 600 }}
                        />
                      ) : (
                        '—'
                      )}
                    </TableCell>
                    <TableCell>
                      {live && code.current_period_end
                        ? new Date(code.current_period_end).toLocaleDateString()
                        : '—'}
                    </TableCell>
                    <TableCell>
                      {code.created_at ? new Date(code.created_at).toLocaleDateString() : '—'}
                    </TableCell>
                    <TableCell align="right">
                      {live && (
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          sx={{ textTransform: 'none', borderRadius: '8px' }}
                          onClick={() => setCancelTarget(code)}
                        >
                          Cancel
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      ))}

      <Dialog open={!!cancelTarget} onClose={() => !cancelling && setCancelTarget(null)}>
        <DialogTitle>Cancel this access code?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This ends the monthly billing for{' '}
            <strong>{cancelTarget?.client_email || 'this client'}</strong> and severs
            your connection to their MindVault data. The client keeps their account
            and all their data. You can buy a new code later to reconnect.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelTarget(null)} disabled={cancelling} sx={{ textTransform: 'none' }}>
            Keep connection
          </Button>
          <Button
            onClick={handleCancelConfirm}
            color="error"
            variant="contained"
            disabled={cancelling}
            sx={{ textTransform: 'none' }}
          >
            {cancelling ? 'Cancelling…' : 'Cancel access code'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Phase 5 credit-cancel confirm (flag-gated): cancels through the RPC,
          which is idempotent and immediate; no refund for the current month. */}
      {PHASE45_COMMS_ENABLED && (
        <Dialog open={!!creditCancelTarget} onClose={() => !creditCancelling && setCreditCancelTarget(null)}>
          <DialogTitle>Cancel this credit connection?</DialogTitle>
          <DialogContent>
            <DialogContentText>
              This ends the connection immediately and stops future renewals.{' '}
              <strong>{creditCancelTarget?.client_email || 'This client'}</strong>{' '}
              keeps their account and all their data. You can add the client again
              later.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCreditCancelTarget(null)} disabled={creditCancelling} sx={{ textTransform: 'none' }}>
              Keep connection
            </Button>
            <Button
              onClick={handleCreditCancelConfirm}
              color="error"
              variant="contained"
              disabled={creditCancelling}
              sx={{ textTransform: 'none' }}
            >
              {creditCancelling ? 'Cancelling…' : 'Cancel connection'}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
};

export default AccessCodesSection;
